import { Box } from '@mui/material';
import type { FC } from 'react';

import { ArchiveButton } from '../Archive/ArchiveButton';
import { FOOTER_BUTTON_SX, SlabButton } from '../SlabButton/SlabButton';

type Props = {
  isArchived: boolean;
  isNew: boolean;
  hasEntity: boolean;
  isArchiveLoading: boolean;
  isSaving: boolean;
  isSaveDisabled: boolean;
  isResetDisabled: boolean;
  canArchive?: boolean;
  onArchive: () => void;
  onCancel: () => void;
  onReset: () => void;
  onSave: () => void;
};

/**
 * Shared footer for entity drawer forms. Renders archive/restore, cancel, reset,
 * and save actions, toggling which buttons appear based on the archived and new
 * states of the entity.
 */
export const DrawerFormFooter: FC<Props> = ({
  isArchived,
  isNew,
  hasEntity,
  isArchiveLoading,
  isSaving,
  isSaveDisabled,
  isResetDisabled,
  canArchive = true,
  onArchive,
  onCancel,
  onReset,
  onSave,
}) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
    {!isArchived && !isNew && hasEntity && (
      <ArchiveButton
        sx={FOOTER_BUTTON_SX}
        isArchived={isArchived}
        isLoading={isArchiveLoading}
        canArchive={canArchive}
        onClick={onArchive}
      />
    )}
    <SlabButton
      sx={FOOTER_BUTTON_SX}
      variant='slab-secondary'
      onClick={onCancel}
      disabled={isSaving || isArchiveLoading}
    >
      Cancel
    </SlabButton>
    {!isArchived && (
      <SlabButton
        sx={FOOTER_BUTTON_SX}
        variant='slab-secondary'
        onClick={onReset}
        disabled={isResetDisabled}
      >
        Reset
      </SlabButton>
    )}
    {!isArchived && (
      <SlabButton
        sx={FOOTER_BUTTON_SX}
        variant='slab-primary'
        onClick={onSave}
        loading={isSaving}
        disabled={isSaveDisabled}
      >
        Save
      </SlabButton>
    )}
    {isArchived && !isNew && hasEntity && canArchive && (
      <SlabButton
        sx={FOOTER_BUTTON_SX}
        variant='slab-primary'
        onClick={onArchive}
        disabled={isArchiveLoading}
        loading={isArchiveLoading}
      >
        Restore
      </SlabButton>
    )}
  </Box>
);
